import { env } from '@/lib/env';
import { ApiError } from '@/lib/api-error';
import type { ApiEnvelope, ApiFailure, ApiSuccess, Paginated } from '@/types/api';

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** Serialised into a query string; `undefined` and `null` are dropped. */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Abort the request after this many milliseconds. */
  timeoutMs?: number;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const DEFAULT_TIMEOUT_MS = 30_000;

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = env.apiBaseUrl.replace(/\/$/, '');
  const url = `${base}/${path.replace(/^\//, '')}`;

  if (!params) {
    return url;
  }

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  }

  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

/**
 * Normalises any `HeadersInit` into a `Headers` instance.
 *
 * Spreading the caller's headers into an object literal looks equivalent but
 * silently corrupts the request when they arrive as the array form of
 * `HeadersInit` - the spread turns `[['a','b']]` into `{ '0': [...] }`.
 */
function buildHeaders(base: Record<string, string>, extra: HeadersInit | undefined): Headers {
  const headers = new Headers(base);

  const zone = viewerTimeZone();
  if (zone) {
    headers.set('X-Time-Zone', zone);
  }

  if (extra) {
    new Headers(extra).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
}

/**
 * The reader's IANA zone, sent with every request.
 *
 * The server stores dates in UTC and must keep doing so, but "how many days
 * until this expires" has to be answered from the calendar the reader lives in.
 * Without this the server counted from UTC's today, which east of UTC is
 * yesterday for the last hours of every local day - so the same trade licence
 * read "Lapsed yesterday" on one screen and "Lapsed 2 days ago" on another,
 * because that second screen counted locally in the browser.
 *
 * On every request rather than once at sign-in: somebody who travels, or whose
 * machine corrects its clock, must not keep being told the old zone's answers
 * for the rest of their session.
 */
function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    /* A runtime without a resolvable zone gets the server's fallback rather
       than a failed request. */
    return undefined;
  }
}

/* ------------------------------------------------------------------------ *
 * Session renewal
 * ------------------------------------------------------------------------ */

/**
 * Paths that must never trigger a refresh attempt: refreshing after a failed
 * sign-in is pointless, and refreshing after a failed refresh would recurse.
 */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

/**
 * The in-flight refresh, shared by every caller.
 *
 * When an access token expires, several queries usually fail at once. Without
 * this they would each fire their own refresh, and because the server rotates
 * the refresh token on use, the later ones would present a token already spent
 * - signing the user out at the exact moment the session was meant to be
 * renewed. One refresh, awaited by all.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      /* Cleared as soon as the attempt settles. Callers already hold this
         promise object, so they still all resolve from this one attempt; a
         request that fails *after* it settles should start a fresh attempt
         rather than reuse a stale result. */
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/* ------------------------------------------------------------------------ *
 * Core request
 * ------------------------------------------------------------------------ */

/** One attempt. Returns the whole success envelope so `list` keeps its meta. */
async function send<T>(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<ApiSuccess<T>> {
  const { params, timeoutMs = DEFAULT_TIMEOUT_MS, headers, signal, ...rest } = options;

  /* Combine the caller's signal with our timeout so either can abort. */
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  const isFormData = body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      ...rest,
      method,
      signal: combinedSignal,
      /* Session cookies ride along; the API sets CORS credentials to match. */
      credentials: 'include',
      headers: buildHeaders(
        {
          Accept: 'application/json',
          /* FormData must set its own multipart boundary, so never override it. */
          ...(!isFormData && body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        headers,
      ),
      body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    const timedOut = cause instanceof DOMException && cause.name === 'TimeoutError';
    throw new ApiError({
      message: timedOut
        ? 'The request took too long. Please try again.'
        : 'Could not reach the server. Check your connection and try again.',
      code: 'SERVICE_UNAVAILABLE',
      status: 0,
    });
  }

  if (response.status === 204) {
    return { success: true, data: undefined as T };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError({
      message: 'The server returned an unreadable response.',
      code: 'INTERNAL_ERROR',
      status: response.status,
      requestId: response.headers.get('x-request-id') ?? undefined,
    });
  }

  const envelope = payload as ApiEnvelope<T>;

  if (!response.ok || !envelope.success) {
    const failure = !envelope.success ? envelope : undefined;
    throw new ApiError({
      message: failure?.error.message ?? 'Something went wrong. Please try again.',
      code: failure?.error.code ?? 'INTERNAL_ERROR',
      status: response.status,
      details: failure?.error.details,
      requestId: failure?.requestId ?? response.headers.get('x-request-id') ?? undefined,
    });
  }

  return envelope;
}

/**
 * Single point of contact with the API.
 *
 * Unwraps the backend envelope, converts every failure into an
 * {@link ApiError}, and renews an expired session once before giving up.
 * Nothing else in the app should call `fetch` against the API.
 */
async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<ApiSuccess<T>> {
  try {
    return await send<T>(method, path, body, options);
  } catch (error) {
    const expiredSession =
      error instanceof ApiError &&
      error.status === 401 &&
      !NO_REFRESH_PATHS.some((skipped) => path.startsWith(skipped));

    if (!expiredSession) {
      throw error;
    }

    /**
     * A FormData body is a stream that the first attempt already consumed, so
     * replaying it would upload nothing. Surface the 401 instead and let the
     * user repeat the upload against the renewed session.
     */
    if (body instanceof FormData || !(await refreshSession())) {
      throw error;
    }

    return send<T>(method, path, body, options);
  }
}

/**
 * Fetches a binary response rather than a JSON envelope.
 *
 * Used by the document viewer: the bytes are pulled over XHR with the session
 * cookie, then rendered from a blob URL the app creates itself. That is the
 * point - the browser is handed a blob whose type *this* code chose from a
 * narrow allowlist, so a stored file can never be interpreted as anything
 * other than the image or PDF it was verified to be.
 *
 * Failures still arrive as the usual JSON envelope, so they are unwrapped
 * into an {@link ApiError} exactly as everywhere else.
 */
async function sendBinary(path: string, options: RequestOptions): Promise<Blob> {
  const { params, timeoutMs = DEFAULT_TIMEOUT_MS, headers, signal, ...rest } = options;

  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      ...rest,
      method: 'GET',
      signal: combinedSignal,
      credentials: 'include',
      headers: buildHeaders({ Accept: '*/*' }, headers),
    });
  } catch (cause) {
    const timedOut = cause instanceof DOMException && cause.name === 'TimeoutError';
    throw new ApiError({
      message: timedOut
        ? 'The file took too long to load. Please try again.'
        : 'Could not reach the server. Check your connection and try again.',
      code: 'SERVICE_UNAVAILABLE',
      status: 0,
    });
  }

  if (!response.ok) {
    let failure: ApiFailure | undefined;
    try {
      failure = (await response.json()) as typeof failure;
    } catch {
      /* A non-JSON error body tells us nothing; the status still does. */
    }

    throw new ApiError({
      message: failure?.error.message ?? 'The file could not be opened.',
      code: failure?.error.code ?? 'INTERNAL_ERROR',
      status: response.status,
      requestId: response.headers.get('x-request-id') ?? undefined,
    });
  }

  return response.blob();
}

export const apiClient = {
  get: async <T>(path: string, options?: RequestOptions): Promise<T> =>
    (await request<T>('GET', path, undefined, options)).data,

  post: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    (await request<T>('POST', path, body, options)).data,

  patch: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    (await request<T>('PATCH', path, body, options)).data,

  put: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    (await request<T>('PUT', path, body, options)).data,

  delete: async <T>(path: string, options?: RequestOptions): Promise<T> =>
    (await request<T>('DELETE', path, undefined, options)).data,

  /**
   * DELETE with a request body. Used by the destructive actions, which require
   * the caller to echo back the tenant code or username as confirmation.
   */
  deleteWithBody: async <T>(path: string, body: unknown, options?: RequestOptions): Promise<T> =>
    (await request<T>('DELETE', path, body, options)).data,

  /**
   * List endpoints return their pagination metadata alongside the rows, which
   * the plain helpers discard. Use this when a table needs the page count.
   */
  list: async <T>(path: string, options?: RequestOptions): Promise<Paginated<T>> => {
    const envelope = await request<T[]>('GET', path, undefined, options ?? {});

    return {
      items: envelope.data,
      /* A list endpoint should always send meta; this keeps a single-page
         response usable if one ever does not. */
      meta: envelope.meta ?? {
        page: 1,
        limit: envelope.data.length,
        total: envelope.data.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  },

  /**
   * GETs a file as a Blob, renewing an expired session once like every other
   * request. Unlike a form upload there is no body to replay, so the retry is
   * always safe here.
   */
  getBlob: async (path: string, options: RequestOptions = {}): Promise<Blob> => {
    try {
      return await sendBinary(path, options);
    } catch (error) {
      const expiredSession = error instanceof ApiError && error.status === 401;

      if (!expiredSession || !(await refreshSession())) {
        throw error;
      }

      return sendBinary(path, options);
    }
  },
};
